import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Check, Loader2, Crown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { detectCurrency, CURRENCY_SYMBOL, formatMoney } from "@/lib/currency";
import Lymphie from "./Lymphie";

const PERKS = [
  "Unlimited chat with Lymphie",
  "Unlimited photo & video uploads",
  "Multi-month PDF exports for your GP",
  "Family / carer sharing",
  "Full access to the Emergency features",
];

const CURRENCIES = [
  { code: "gbp", label: "GBP £" },
  { code: "usd", label: "USD $" },
  { code: "eur", label: "EUR €" },
];

export default function UpgradeModal({ onClose }) {
  const [billingInterval, setBillingInterval] = useState("yearly"); // 'monthly' | 'yearly'
  const [currency, setCurrency] = useState(detectCurrency());
  const [prices, setPrices] = useState({ monthly: 399, yearly: 3900 });
  const [trialDays, setTrialDays] = useState(7);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/config").then((r) => {
      setTrialDays(r.data.trial_days || 7);
      const p = r.data.subscription_prices?.[currency] || r.data.subscription_prices?.gbp;
      if (p) setPrices(p);
    });
  }, [currency]);

  const monthlyEquivalent = billingInterval === "yearly" ? (prices.yearly / 12) : prices.monthly;

  const checkout = async () => {
    setBusy(true);
    try {
      const lookup = `pro_${billingInterval}_${currency}`;
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookup,
        origin_url: window.location.origin,
      });
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
      setBusy(false);
    }
  };

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[55] bg-kawaii-text/40 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="upgrade-modal"
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ scale: 0.94, y: 10 }} animate={{ scale: 1, y: 0 }}
        className="kawaii-card max-w-lg w-full my-6 p-6 md:p-8 relative"
      >
        <button
          onClick={onClose}
          data-testid="close-upgrade-btn"
          className="kawaii-btn absolute top-4 right-4 w-9 h-9 rounded-full bg-white border border-kawaii-border flex items-center justify-center"
        >
          <X className="w-4 h-4" strokeWidth={2.5} />
        </button>

        <div className="flex items-center gap-3 mb-2">
          <Lymphie size={48} />
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-kawaii-textSoft font-bold">Try Pro</p>
            <h2 className="font-heading text-2xl md:text-3xl text-kawaii-text">Unlock the full Lymphie ♡</h2>
          </div>
        </div>

        <div className="rounded-2xl bg-kawaii-lavender/15 border border-kawaii-lavender/40 p-3 mt-4 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-kawaii-lavender" strokeWidth={2.5} />
          <p className="text-xs font-body text-kawaii-text">
            <strong>{trialDays}-day free trial</strong> — cancel anytime before it ends, no charge.
          </p>
        </div>

        <ul className="mt-5 space-y-2">
          {PERKS.map((p) => (
            <li key={p} className="flex items-center gap-2 text-sm text-kawaii-text font-body">
              <Check className="w-4 h-4 text-kawaii-lavender flex-shrink-0" strokeWidth={2.5} />
              {p}
            </li>
          ))}
        </ul>

        {/* Interval toggle */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          {[
            { key: "monthly", label: "Monthly", tag: null },
            { key: "yearly",  label: "Yearly",  tag: "Save ~18%" },
          ].map((i) => {
            const active = billingInterval === i.key;
            const amt = i.key === "monthly" ? prices.monthly : prices.yearly;
            return (
              <button
                key={i.key}
                onClick={() => setBillingInterval(i.key)}
                data-testid={`interval-${i.key}`}
                className={`kawaii-btn p-3 rounded-2xl text-left border ${
                  active ? 'bg-white border-kawaii-lavender ring-2 ring-kawaii-lavender/40' : 'bg-white/70 border-kawaii-border'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-heading font-semibold uppercase tracking-wider text-kawaii-text">{i.label}</span>
                  {i.tag && (
                    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-kawaii-mint/70 text-kawaii-text">{i.tag}</span>
                  )}
                </div>
                <div className="mt-1 font-heading text-2xl text-kawaii-text">{formatMoney(amt, currency)}</div>
                <div className="text-[11px] font-body text-kawaii-textSoft">per {i.key === "monthly" ? "month" : "year"}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] text-kawaii-textSoft font-body italic">
            {billingInterval === "yearly" ? `≈ ${formatMoney(monthlyEquivalent, currency)}/mo` : ""}
          </span>
          <div className="flex items-center gap-1.5 rounded-full bg-white/70 border border-kawaii-border p-1">
            {CURRENCIES.map((c) => (
              <button
                key={c.code}
                onClick={() => setCurrency(c.code)}
                data-testid={`currency-${c.code}`}
                className={`kawaii-btn text-[11px] px-2.5 py-1 rounded-full font-body font-semibold ${
                  currency === c.code ? 'bg-kawaii-lavender/30 text-kawaii-text' : 'text-kawaii-textSoft'
                }`}
              >{c.label}</button>
            ))}
          </div>
        </div>

        <button
          onClick={checkout}
          disabled={busy}
          data-testid="checkout-btn"
          className="kawaii-btn mt-6 w-full py-3 rounded-full bg-gradient-to-r from-kawaii-pink via-kawaii-peach to-kawaii-lavender text-white font-heading font-semibold text-sm shadow-md inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" strokeWidth={2.5} />}
          Start my {trialDays}-day free trial
        </button>

        <p className="text-[10px] text-kawaii-textSoft font-body italic text-center mt-3">
          Secure checkout by Stripe. Cancel any time from your account.
        </p>
      </motion.div>
    </motion.div>,
    document.body
  );
}
